import React, { useLayoutEffect, useState } from 'react';
import Lottie from 'lottie-react';
import defaultLoaderAnimation from '../../assets/lottie/generic-loader.json';

const SIZES = {
  small: { width: 80, height: 80 },
  medium: { width: 120, height: 120 },
  large: { width: 450, height: 450 },
};

// Synchronously read the sidebar width before paint to avoid flash.
// Start at the sidebar's real right edge, not just where .mainContent begins:
// the expanded rail is 220px while the content margin is 210px, so an overlay
// starting at 210 painted over the sidebar's border (user 2026-09-10).
const PAGE_GUTTER_X = 20; // .appPageContainer px-5
const PAGE_GUTTER_Y = 16; // .appPageContainer py-4

const measure = () => {
  if (typeof document === 'undefined') return { overlayLeft: 0, frameLeft: PAGE_GUTTER_X, framed: false };
  const mainContent = document.querySelector('.mainContent');
  const sidebar = document.querySelector('.appSidebar');
  const sidebarRect = sidebar ? sidebar.getBoundingClientRect() : null;
  const contentLeft = mainContent ? mainContent.getBoundingClientRect().left : 0;
  const sidebarRight = sidebarRect ? sidebarRect.right : 0;
  const overlayLeft = Math.max(contentLeft, sidebarRight);
  // The page frame is drawn only inside the desktop sidebar layout, where a
  // page's ThemeMainWrapper would sit one gutter inside .mainContent
  // (x = 230 with the expanded rail). Mobile (sidebar display:none, fixed
  // header on top) and public pages keep the plain translucent sheet.
  const framed = !!mainContent && !!sidebarRect && sidebarRect.width > 0;
  return { overlayLeft, frameLeft: Math.max(0, contentLeft + PAGE_GUTTER_X - overlayLeft), framed };
};
const getContentLeft = () => measure().overlayLeft;

/**
 * LottieLoader — drop-in replacement for Ant Design <Spin>.
 *
 * Standalone:  <LottieLoader size="large" text="Loading..." />
 * Wrapper:     <LottieLoader spinning={loading} text="Loading...">content</LottieLoader>
 * Inline:      <LottieLoader inline spinning={loading}>content</LottieLoader>
 * Custom:      <LottieLoader animationData={myLottie} size="medium" />
 */
const LottieLoader = ({
  animationData = defaultLoaderAnimation,
  size = 'large',
  text,
  spinning,
  children,
  style,
  inline = false,
}) => {
  const dimensions = typeof size === 'number' ? { width: size, height: size } : (SIZES[size] || SIZES.large);
  const [geom, setGeom] = useState(() => (inline ? { overlayLeft: 0, frameLeft: PAGE_GUTTER_X, framed: false } : measure()));
  const contentLeft = geom.overlayLeft;

  useLayoutEffect(() => {
    if (inline) return undefined;
    setGeom(measure());
    const handleResize = () => setGeom(measure());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [inline, spinning]);

  const lottieElement = (
    <Lottie
      animationData={animationData}
      loop
      autoplay
      style={{ width: dimensions.width, height: dimensions.height }}
    />
  );

  const overlayBase = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
  };

  const fullScreenOverlay = { ...overlayBase, position: 'fixed', top: 0, left: contentLeft, right: 0, bottom: 0, zIndex: 9999 };

  const inlineContent = (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 8, padding: '40px 0', ...style }}>
      {lottieElement}
      {text && <span style={{ fontSize: 14, color: '#485066', fontWeight: 500 }}>{text}</span>}
    </div>
  );

  // Wrapper mode: <LottieLoader spinning={bool}>children</LottieLoader>
  if (children !== undefined) {
    if (!spinning) return <>{children}</>;
    if (inline) return inlineContent;
    return (
      <div style={{ ...style }}>
        {children}
        <div style={fullScreenOverlay}>
          {lottieElement}
          {text && <span style={{ fontSize: 14, color: '#485066', fontWeight: 500 }}>{text}</span>}
        </div>
      </div>
    );
  }

  // Standalone mode: <LottieLoader size="large" text="Loading..." />
  // The page has not rendered yet, so draw the same bordered page frame
  // (ThemeMainWrapper: 16px/20px page gutter, 1px #E2E8EE border, 10px
  // radius) around the animation. Without it the frame edge next to the
  // sidebar vanished while a page loaded (user 2026-09-10).
  if (inline) return inlineContent;
  if (!geom.framed) {
    return (
      <div style={style}>
        <div style={fullScreenOverlay}>
          {lottieElement}
          {text && <span style={{ fontSize: 14, color: '#485066', fontWeight: 500 }}>{text}</span>}
        </div>
      </div>
    );
  }
  const pageFrame = {
    position: 'absolute',
    top: PAGE_GUTTER_Y,
    right: PAGE_GUTTER_X,
    bottom: PAGE_GUTTER_Y,
    left: geom.frameLeft,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    background: '#FFFFFF',
    border: '1px solid #E2E8EE',
    borderRadius: 10,
  };
  return (
    <div style={style}>
      <div style={fullScreenOverlay}>
        <div style={pageFrame}>
          {lottieElement}
          {text && <span style={{ fontSize: 14, color: '#485066', fontWeight: 500 }}>{text}</span>}
        </div>
      </div>
    </div>
  );
};

export default LottieLoader;
